import { ExprCall, Ident } from '@mode-switch/express';
import { EncField, Encoding, EncSort, SortOrder, SortType, VariableType } from '@mode-vegas/vegas';
import { FlamingoTypes } from '@mode/shared/contract-common';
import { CompiledAttr, CompiledFormula } from '@mode/shared/util-switch';
import * as FieldHelpers from '../helpers/field-helpers';

type ModeView = FlamingoTypes.ModeView;
export const MAX_SORTABLE_VALUES = 200;

export interface SortConfig {
  type: SortType;
  order: SortOrder;
  callName: ExprCall.Name | undefined;
  attrName: string | undefined;
  items?: unknown[];
}

export function isValidConfig(value: SortConfig) {
  switch (value.type) {
    case SortType.ALPHA:
    case SortType.DATA: {
      return value.order != null;
    }
    case SortType.MANUAL: {
      return value.items != null && value.order === SortOrder.ASC;
    }
    case SortType.NESTED:
    case SortType.FIELD: {
      return value.order != null && value.attrName != null && value.callName != null;
    }
    default: {
      return false;
    }
  }
}

export function makeSortConfig(view: ModeView, field: EncField, sort?: EncSort): SortConfig {
  sort ??= {
    type: SortType.DATA,
    source: field.formula,
    descending: false,
  };

  switch (sort.type) {
    case SortType.ALPHA:
    case SortType.DATA: {
      return {
        type: sort.type,
        order: order(sort),
        callName: undefined,
        attrName: undefined,
      };
    }
    case SortType.MANUAL: {
      return {
        type: sort.type,
        order: SortOrder.ASC,
        callName: undefined,
        attrName: undefined,
        items: [...sort.items],
      };
    }
    case SortType.NESTED:
    case SortType.FIELD: {
      if (sort.byField == null) {
        throw new Error(`sort.byField must be defined in a by-field sort`);
      }

      const compiled = FieldHelpers.compileField(view, sort.byField);
      if (FieldHelpers.isValidFormula(compiled)) {
        return {
          type: sort.type,
          order: order(sort),
          callName: callName(compiled),
          attrName: attrName(compiled),
        };
      }
      // Sort config isn't valid :/
      else {
        return {
          type: sort.type,
          order: order(sort),
          callName: undefined,
          attrName: undefined,
        };
      }
    }
  }

  ///
  ///

  function order(sort: EncSort): SortOrder {
    return sort.descending ? SortOrder.DESC : SortOrder.ASC;
  }

  function attrName(compiled: CompiledFormula): string | undefined {
    if (compiled.attrName != null) {
      return compiled.attrName;
    }
  }

  function callName(compiled: CompiledFormula): ExprCall.Name | undefined {
    if (compiled.attrName != null) {
      let name = <ExprCall.Name>compiled.aggFunc;
      if (name == null) {
        const fns = FieldHelpers.aggFnsForFormula(compiled);
        name = fns[0].value;
      }
      return ExprCall.Name.make(name ?? '');
    }
  }
}

export function makeSort(field: EncField, data: SortConfig): EncSort | undefined {
  const desc = data.order === SortOrder.DESC;
  switch (data.type) {
    case SortType.ALPHA: {
      return EncSort.ByAlpha.make(field.formula, desc);
    }
    case SortType.DATA: {
      return EncSort.ByData.make(field.formula, desc);
    }
    case SortType.MANUAL: {
      return EncSort.ByManual.make(field.formula, data.items?.slice(0, MAX_SORTABLE_VALUES) ?? [], false);
    }
    case SortType.FIELD: {
      let formula = `[${data.attrName}]`;
      if (ExprCall.Name.isKnown(data.callName ?? '')) {
        formula = `${data.callName}(${formula})`;
      }

      const byField = {
        id: Ident.next('ENC'),
        formula,
        variable: {
          type: VariableType.DISCRETE,
        },
      };

      return EncSort.ByField.make(field.formula, byField, desc);
    }
    case SortType.NESTED: {
      let formula = `[${data.attrName}]`;
      if (ExprCall.Name.isKnown(data.callName ?? '')) {
        formula = `${data.callName}(${formula})`;
      }

      const byField = {
        id: Ident.next('ENC'),
        formula,
        variable: {
          type: VariableType.DISCRETE,
        },
      };

      if (field.id != null) {
        return EncSort.ByNested.make(field.id, field.formula, byField, desc);
      }
      break;
    }
    default: {
      return;
    }
  }
}

export function fieldAllowsNestedSort(enc: Encoding, field: EncField | string): boolean {
  const alias = typeof field === 'string' ? field : (field.id as string);
  if (typeof alias === 'string') {
    return [enc.column, enc.x, enc.row, enc.y].some((channel) => {
      return channel.some((f) => f.id === alias);
    });
  }
  return false;
}

///
///
export type SortTypeOpt = FieldHelpers.IOpt<SortType>;
export type SortOrderOpt = FieldHelpers.IOpt<SortOrder>;
export type SortAggFnOpt = FieldHelpers.IOpt<ExprCall.Name>;
export type SortFieldOpt = FieldHelpers.IOpt<CompiledAttr>;
export type SortOpt = SortTypeOpt | SortOrderOpt | SortAggFnOpt | SortFieldOpt;

export const sortTypeOptions: ReadonlyArray<SortTypeOpt> = [
  // ** Doesn't work in the Switch BE. (bugz?)
  // ** Will come back to this
  // {
  //   name: 'Alphabetic',
  //   value: SortType.ALPHA,
  // },
  {
    name: 'Data',
    value: SortType.DATA,
  },
  {
    name: 'Field',
    value: SortType.FIELD,
  },
  {
    name: 'Nested',
    value: SortType.NESTED,
  },
  {
    name: 'Manual',
    value: SortType.MANUAL,
  },
];

export const sortOrderOptions: ReadonlyArray<SortOrderOpt> = [
  {
    name: 'Ascending',
    value: SortOrder.ASC,
  },
  {
    name: 'Descending',
    value: SortOrder.DESC,
  },
];
